import api from './api';

export const inventoryService = {
  // Get all inventory items
  async getAllInventory() {
    try {
      const response = await api.get('/inventory');
      return response.data;
    } catch (error) {
      console.error('Error fetching inventory:', error);
      
      // Fallback to localStorage if API fails
      const storedInventory = localStorage.getItem('stockpro_inventory');
      if (storedInventory) {
        return JSON.parse(storedInventory);
      }
      
      throw error;
    }
  },

  // Get inventory item by ID
  async getInventoryById(id) {
    try {
      const response = await api.get(`/inventory/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching inventory item ${id}:`, error);
      
      // Fallback to localStorage
      const storedInventory = localStorage.getItem('stockpro_inventory');
      if (storedInventory) {
        const items = JSON.parse(storedInventory);
        const item = items.find(i => i.id === id);
        if (item) return item;
      }
      
      throw error;
    }
  },

  // Add or update inventory item
  async addInventoryItem(inventoryData) {
    try {
      const response = await api.post('/inventory', inventoryData);
      return response.data;
    } catch (error) {
      console.error('Error adding inventory item:', error);
      throw error;
    }
  },

  // Update inventory item
  async updateInventoryItem(id, updates) {
    try {
      const response = await api.put(`/inventory/${id}`, updates);
      return response.data;
    } catch (error) {
      console.error('Error updating inventory item:', error);
      throw error;
    }
  },

  // Delete inventory item
  async deleteInventoryItem(id) {
    try {
      await api.delete(`/inventory/${id}`);
      return true;
    } catch (error) {
      console.error('Error deleting inventory item:', error);
      throw error;
    }
  },

  // Get inventory for a specific product
  async getInventoryByProductId(productId) {
    try {
      const allInventory = await this.getAllInventory();
      return allInventory.filter(item => item.productId === productId);
    } catch (error) {
      console.error('Error fetching inventory for product:', error);
      throw error;
    }
  },

  // Update product stock through inventory
  async updateProductStock(productId, quantity, operation = 'set', location = 'Entrepôt principal', notes = '') {
    try {
      // First get current inventory for this product
      const currentInventory = await this.getInventoryByProductId(productId);
      
      if (currentInventory.length === 0) {
        // Create new inventory entry
        return await this.addInventoryItem({
          productId,
          quantity: operation === 'set' ? quantity : (operation === 'add' ? quantity : 0),
          location,
          notes,
          lastUpdated: new Date().toISOString()
        });
      } else {
        // Update existing inventory
        const existing = currentInventory[0];
        let newQuantity;
        
        switch (operation) {
          case 'add':
            newQuantity = existing.quantity + quantity;
            break;
          case 'subtract':
            newQuantity = Math.max(0, existing.quantity - quantity);
            break;
          case 'set':
          default:
            newQuantity = quantity;
            break;
        }
        
        return await this.updateInventoryItem(existing.id, {
          quantity: newQuantity,
          lastUpdated: new Date().toISOString(),
          notes: notes || existing.notes
        });
      }
    } catch (error) {
      console.error('Error updating product stock:', error);
      throw error;
    }
  },

  // Sync inventory with products data
  async syncWithProducts(products) {
    try {
      const currentInventory = await this.getAllInventory();
      const promises = [];

      for (const product of products) {
        const existingInventory = currentInventory.find(inv => inv.productId === product.id);
        
        if (!existingInventory) {
          // Create inventory entry for products that don't have one
          promises.push(this.addInventoryItem({
            productId: product.id,
            quantity: product.stock || 0,
            location: 'Entrepôt principal',
            notes: 'Sync automatique avec produits',
            lastUpdated: new Date().toISOString()
          }));
        } else if (existingInventory.quantity !== product.stock) {
          // Update inventory if quantities don't match
          promises.push(this.updateInventoryItem(existingInventory.id, {
            quantity: product.stock || 0,
            lastUpdated: new Date().toISOString(),
            notes: `Sync automatique: ${existingInventory.quantity} -> ${product.stock}`
          }));
        }
      }

      const results = await Promise.all(promises);
      console.log(`Synced ${results.length} inventory items with products`);
      return results;
    } catch (error) {
      console.error('Error syncing inventory with products:', error);
      throw error;
    }
  }
};

export default inventoryService;